import { useCallback, useRef } from 'react';

export interface IClassHelper {
  (...classes: Array<string | { [key: string]: boolean }>): string;
}

export const useClassHelper = (baseClass: string): IClassHelper => {
  const memoizedClassesRef = useRef<{ [key: string]: string }>({});

  const classHelper: IClassHelper = useCallback(
    (...classes) => {
      const classKey = JSON.stringify(classes);
      if (memoizedClassesRef.current[classKey]) {
        return memoizedClassesRef.current[classKey];
      }

      const computedClasses = classes
        .filter(Boolean)
        .flatMap(className => {
          if (className === 'base') {
            return baseClass;
          }

          if (typeof className === 'string') {
            if (!className.startsWith('_') && !className.startsWith('-')) {
              return `${className}`;
            }

            return `${baseClass}${className}`;
          } else if (typeof className === 'object') {
            return Object.entries(className)
              .filter(([_, condition]) => condition)
              .map(([key]) => {
                if (key === 'base') {
                  return baseClass;
                }

                if (!key.startsWith('_') && !key.startsWith('-')) {
                  return `${key}`;
                }

                return `${baseClass}${key}`;
              });
          }

          return '';
        })
        .join(' ')
        .trim();

      memoizedClassesRef.current[classKey] = computedClasses;

      return computedClasses;
    },
    [baseClass],
  );

  (classHelper as any).baseClass = baseClass;

  return classHelper;
};
